/*
 * @Author: 谭聪 156638174@qq.com
 * @FilePath: /mall-fe/src/comps/FullProductCardItem.tsx
 * @Description: 显示完整的产品卡片项目
 *
 */

import useRateAndSellCount from 'hooks/useRateAndSellCount';
import useUrl from 'hooks/useUrl';
import Dian from 'icons/store-icon.svg';
import GreenTag from 'icons/green/green_tag.svg';
import { RuntimeConfigurationContext, SiteContext, UserContext } from 'libs/contexts';
import { SITE_MODE } from 'libs/enums';
import { resizeImage } from 'libs/urls';
import { formatPrice } from 'libs/utils';
import { useContext, useEffect, useState } from 'react';
import { cls } from 'reactutils';
import PlaceholderImage from './PlaceholderImage';
import { useProductListNetPriceContext } from 'libs/contexts';
import { useRouter } from 'next/router';
import { UserSource } from 'libs/enums';

type FullProductCardItemProps = {
  data: SpuEntity;
  children?: any;
};
export const ProductPrice = ({ data, fontSize }: any) => {
  const router = useRouter();
  const { shopSysNo } = router?.query || {};
  const netPriceListObj = useProductListNetPriceContext();
  const netPrice = netPriceListObj?.ProductListNetPrice?.find(
    (v) => v.skuSysNo === data.bestSkuSysNo,
  );
  const price = netPrice?.price || data?.lowestPrice;
  // 格式化价格
  const salePrice = !netPriceListObj?.loading ? formatPrice(price) : '';
  const userInfo = useContext(UserContext);
  const [isSpecialShop, setIsSpecialShop] = useState(false);
  useEffect(() => {
    if (shopSysNo === '1047548387118899707') {
      // 创新专区店铺特殊处理，不显示价格@张政需求
      setIsSpecialShop(true);
    } else {
      setIsSpecialShop(false);
    }
  }, [shopSysNo]);

  const hidePrice = userInfo?.organizationSource === UserSource.outside && (!data.searchPriceFlag || !netPrice?.price);
  return (
    <div className="grid-item-price">
      <span className="currency">¥</span>
      {!userInfo?.name ? <span className="text">登录后可见</span> : hidePrice ? <span className="text">暂无报价</span> : (
        <>
          <span className="text">{salePrice}</span>
          <span className="separator">/</span>
          <span className="unit">{data?.unit}</span>
        </>
      )}
      <style jsx>{`
      .grid-item-price {
        color: #fa5400;
        margin-top: 10px;
        // line-height: 16px;
        font-size: ${fontSize || 16}px;
        white-space: nowrap;
      }

      .grid-item-price .currency,
      .grid-item-price .separator,
      .grid-item-price .unit {
        font-size: 12px;
        margin-right: 2px;
      }

      .grid-item-price span {
        display: inline-block;
        vertical-align: middle;
      }

      .grid-item-price .hypen,
      .grid-item-price .text {
        font-size: ${fontSize || 16}px;
      }

      .grid-item-price .separator {
        margin-left: 2px;
      }
    `}</style>
    </div>
  );
};

/**
 * React组件，用于显示完整的产品卡片项目
 *
 * @param {Object} data - 产品数据对象，包含产品的各种信息。
 * @returns {JSX.Element} 渲染的React元素，展示完整的产品卡片项目。
 */
export default function FullProductCardItem({ data, children }: FullProductCardItemProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const site = useContext(SiteContext);
  // 获取评分和销售数量的状态
  const { canView } = useRateAndSellCount();
  const { getPublicUrl } = useUrl();
  const rtCtx = useContext(RuntimeConfigurationContext);
  const ossProvider = rtCtx.find((item) => item.key === 'ossProvider')?.value ?? 'obs';
  const publicBucketName = rtCtx.find((config) => config.key === 'public-app-name')?.value;
  const privateBucketName = rtCtx.find((config) => config.key === 'private-app-name')?.value;
  // 处理点击事件，打开产品详情页面
  function handleClick() {
    window.open(`/product/detail?id=${data.bestSkuSysNo}`);
  }

  const isGreenProduct = !!data.productTagServiceList?.find((tag) => {
    return tag.tagName.includes('绿色');
  });

  return (
    <>
      <div onClick={handleClick} title={data.name} className="grid-item clearfix">
        {data.productTagImageList?.map((item, index) => (
          <div className="grid-img-tag" key={index}>
            {item.tagName}
          </div>
        ))}
        {/* 渲染产品预览图像和导航 */}
        <div className="preview J_Preview">
          <div className="preview-bd J_PreviewCurrent">
            <PlaceholderImage
              src={resizeImage(getPublicUrl(data.images?.[activeIndex]), 200, 200, {
                ossProvider,
                publicBucketName,
                privateBucketName,
              })}
              height={'100%'}
              width={'100%'}
            />
            {
              !data.hasTradeAgreementFlag && (
                <div className='not-net-tag'>
                  当前区域不可售
                </div>
              )
            }
          </div>
          <div className="preview-ft J_PreviewNav">
            <div className="preview-items J_PreviewItems">
              <ul className="preview-items-wrap J_PreviewItemsList">
                {data?.images?.map((item, index) => (
                  <li
                    className={cls('preview-item J_PreviewItem', activeIndex === index && 'active')}
                    key={index}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <PlaceholderImage
                      src={resizeImage(getPublicUrl(item), 200, 200, {
                        ossProvider,
                        publicBucketName,
                        privateBucketName,
                      })}
                      height={'100%'}
                      width={'100%'}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {isGreenProduct && (
            <div className="green-tag clearfix">
              <GreenTag height={32} width={32} />
            </div>
          )}
        </div>
        {children || <ProductPrice data={data} fontSize={12} />}
        <div className="grid-item-title">
          {data.productTagTitleList?.map((item, index) => (
            <div key={index} className="grid-title-tag">
              {item.tagName}
            </div>
          ))}
          <span title={data.name}>{data.name}</span>
        </div>
        <div className="grid-item-spec">
          <span title="14">{data.skuCount}种规格</span>
          {canView && <span className="sell-count">销量{data.sellCountString}</span>}
        </div>
        {site?.mall?.enableSelfSiteMode === SITE_MODE.SHOP && (
          <div className="mt10">
            <p className="shop-sign">
              <Dian />
              <span className="shop-name">{data.shopName}</span>
            </p>
          </div>
        )}

        <div className="mt10">
          <span className="upstream-supplier-name-wrap">
            {data.upstreamSupplierName && (
              <>
                <span>由 </span>
                <span className="upstream-supplier-name" title={data.upstreamSupplierName}>
                  {data.upstreamSupplierName}
                </span>
                <span>配送</span>
              </>
            )}
          </span>
        </div>
        <div className="mt10">
          <span className="upstream-supplier-name-wrap">
            {data.upstreamSupplierName && data.upstreamSupplierDeliveryName && (
              <>
                <span style={{flex: 'none'}}>
                  配送商
                  <br />
                  联系人{' '}
                </span>
                <span
                  className="upstream-deliver-name"
                  title={
                    data.upstreamSupplierDeliveryName + ' ' + data.upstreamSupplierDeliveryPhone
                  }
                >
                  {data.upstreamSupplierDeliveryName} {data.upstreamSupplierDeliveryPhone}
                </span>
              </>
            )}
          </span>
        </div>
        {data.productTagServiceList?.length && !isGreenProduct ? (
          <div>
            <div className="grid-service-tag-wrap">
              {data.productTagServiceList?.map((item, index) => (
                <span className="grid-service-tag" key={index}>
                  <span className="border-item" />
                  {item.tagName}
                </span>
              ))}
            </div>
          </div>
        ) : null}
        {/* {data.hasTradeAgreementFlag === false ? (
          <div className="grid-item-tips">
            <span className="warn-content">该商品暂不支持销售</span>
          </div>
        ) : (
          data.saleAreaCan === false && (
            <div className="grid-item-tips">
              <span className="warn-content">暂不支持在当前区域销售</span>
            </div>
          )
        )} */}
        {/* 20230727 优化展示 */}
        {/* {(data.saleAreaCan === false || data.showTagFlag === false) && (
          <div className="grid-item-tips">
            <span className="warn-content">
              {!data.showTagFlag ? '商品暂不支持销售' : '暂不支持在当前区域销售'}
            </span>
          </div>
        )} */}
      </div>
      <style jsx>{`
        .grid-item {
          font-size: 12px;
          float: left;
          display: block;
          width: 224px;
          height: 450px;
          padding: 21px 21px 20px;
          margin-right: 20px;
          margin-bottom: 20px;
          background-color: #fff;
          border: 1px solid transparent;
          will-change: transform;
          transition: transform 0.2s;
          position: relative;
          cursor: pointer;
        }

        .grid-item-tips {
          margin-top: 5px;
        }

        .warn-content {
          background: #ff4848;
          color: #fff;
          padding: 2px 5px;
          border-radius: 3px;
        }

        .green-tag {
          position: absolute; 
          top: 0px;
          right: 5px;
        }

        .not-net-tag {
          position: absolute; 
          bottom: 0px;
          left: 0px;
          background: #fff;
          padding: 3px 3px;
          color: red;
        }

        .preview {
          height: 218px;
          position: relative;
        }

        .preview-bd {
          overflow: hidden;
          width: 180px;
          height: 180px;
          margin-bottom: 8px;
          position: relative;
        }
        .preview-bd img {
          width: 100%;
          height: 100%;
        }

        .preview-ft,
        .preview-items {
          position: relative;
        }

        .preview-items {
          width: auto;
          height: 30px;
          margin: 0px;
          overflow: hidden;
        }

        .preview-items-wrap {
          position: absolute;
          width: 100%;
          top: 0;
          left: 0;
          will-change: left;
        }

        .preview-item {
          float: left;
          width: 60px;
          height: 60px;
          margin-right: 18px;
          border: 1px solid #e8e8e8;
          cursor: pointer;
        }

        .preview-item.active,
        .preview-item.category-item:hover {
          border-width: 1px;
          border-color: var(--primaryColor);
        }

        .grid-item .preview .preview-item {
          width: 30px;
          height: 30px;
          margin-right: 7px !important;
          padding: 1px;
        }

        .grid-item .preview .preview-item:last-child {
          margin-right: 0 !important;
        }

        .preview-item img {
          width: 100%;
          height: 100%;
        }

        .grid-item .grid-item-title {
          color: #333;
          word-break: break-all;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          margin-top: 7px;
          height: 34px;
          max-height: 34px;
          font-weight: 400;
        }

        .grid-item .grid-title-tag {
          padding: 4px;
          background: var(--primaryColor);
          border-radius: 1px;
          margin-right: 7px;
          z-index: 10;
        }

        .grid-item .grid-img-tag,
        .grid-item .grid-title-tag {
          font-size: 12px;
          color: #fff;
          line-height: 1;
          display: inline-block;
        }

        .grid-item .grid-item-company,
        .grid-item .grid-item-spec {
          max-width: 100%;
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          word-wrap: normal;
          white-space: nowrap;
        }

        .grid-item .grid-item-spec {
          line-height: 12px;
          color: #666;
          height: 12px;
        }

        .grid-item .grid-item-company,
        .grid-item .grid-item-spec {
          margin-top: 10px;
        }

        .mt10 {
          margin-top: 10px;
        }

        .shop-sign {
          line-height: 12px;
          font-size: 0;
          width: 100%;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          display: flex;
          align-items: center;
        }

        .shop-sign .mallfont,
        .shop-sign .shop-name {
          display: inline-block;
          vertical-align: middle;
          line-height: 12px;
          font-size: 12px;
        }
        .shop-sign .shop-name {
          color: #999;
          width: calc(100% - 20px);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-left: 4px;
        }
        .upstream-supplier-name-wrap {
          display: flex;
          align-items: center;
          font-size: 12px;
          color: #666;
          height: 17px;
        }

        .upstream-supplier-name-wrap .upstream-supplier-name {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #999;
          max-width: 142px;
        }

        .upstream-supplier-name-wrap .upstream-deliver-name {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #999;
          max-width: 142px;
          margin-left:10px;
        }

        .sell-count {
          float: right;
        }
        .grid-item:hover {
          transform: translate3d(0, -2px, 0);
          box-shadow: 0 2px 8px 0 rgb(0 0 0 / 8%);
        }
        .grid-item .grid-img-tag {
          position: absolute;
          top: 26px;
          left: 26px;
          background-color: rgba(245, 34, 45, 0.9);
          padding: 4px 6px 3px;
          z-index: 10;
        }

        .grid-item .grid-service-tag-wrap {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          height: 23px;
          line-height: 23px;
          display: inline-block;
          max-width: 100%;
          margin-top: 8px;
          position: relative;
        }

        .grid-service-tag {
          padding: 4px;
          border-radius: 1px;
          font-size: 12px;
          color: #f5222d;
          line-height: 1;
          margin-right: 2px;
          position: relative;
        }

        .border-item {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
          border: 1px solid #f5222d;
        }

        .grid-service-tag:last-child .border-item {
          border-right: none;
          margin-right: 0;
        }

        .grid-item .grid-service-tag-wrap:after {
          content: "";
          width: 1px;
          height: 23px;
          background-color: #f5222d;
          position: absolute;
          right: 1px;
          top: 0;
        }
      `}</style>
    </>
  );
}
